import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { erApiFeil, erNettfeil } from '../api/feil.ts';
import { useLagProgramFraHypotese } from '../api/useHypoteseProgrammer.ts';
import { useHypoteseMedNummer } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import type { Product } from '../api/useProducts.ts';
import type { Program, ProgramCreate } from '../api/usePrograms.ts';
import { Varsel } from '../hypoteser/deler.tsx';
import { Knapp } from '../innboks/Detalj.tsx';
import { INNSPILL } from '../skall/rom.ts';
import { tilgangFra } from '../skall/tilgang.ts';
import { utfyllingFra } from './fraHypotese.ts';
import { Skjema, type FraHypotese } from './Skjema.tsx';

/** Navigasjonens tilstand når skjemaet lukkes: detaljen setter fokus på døren hun brukte (skjerm 22). */
export const FOKUS_DOEREN = { fokus: 'programdoer' } as const;

/** «H-142», «h142» eller «142» i `?fra=` → 142. */
export function lesHypoteseNr(fra: string | null): number | null {
  const m = /^(?:h-?)?(\d+)$/i.exec(fra?.trim() ?? '');
  return m ? Number(m[1]) : null;
}

/**
 * «Nytt program» åpnet fra hypotesen (H3.4, konseptsiden del B). Henter hypotesen, fyller ut skjemaet
 * i klienten (`fraHypotese.ts`) og lagrer med `POST /hypotheses/{id}/programs` — først da finnes
 * programmet. «Lukk» og «‹ H-142» går tilbake uten spor.
 *
 * Ukjent tilstand er strengest (skjerm 19): til hypotesen er hentet, står etikettene med låste felt —
 * et felt som ble fylt etter at hun begynte å skrive, ville overskrevet det hun skrev. Feilet hentingen,
 * fylles ingenting ut halvveis (skjerm 20).
 */
export function NyttFraHypotese({
  nr,
  produkter,
  produkterTilstand,
  maksKontakterTak,
  minorsPresent,
  innstillingerTilstand,
  onLagret,
}: {
  nr: number;
  produkter: Product[];
  produkterTilstand: 'laster' | 'feil' | 'ok';
  maksKontakterTak: number;
  minorsPresent: boolean;
  innstillingerTilstand: 'laster' | 'feil' | 'ok';
  onLagret: (program: Program, nr: number) => void;
}) {
  const me = useMe();
  const hypotese = useHypoteseMedNummer(nr);
  const lag = useLagProgramFraHypotese();
  const naviger = useNavigate();
  const lokasjon = useLocation();
  const nrTekst = `H-${nr}`;
  // Tilbake dit hun kom fra (Til deg eller registeret); uten det, til registeret, som alle i rommet når.
  const fraSti = (lokasjon.state as { tilbake?: string } | null)?.tilbake;
  const tilbake = useMemo(() => ({ til: fraSti ?? `/h/registeret/${nr}`, state: FOKUS_DOEREN }), [fraSti, nr]);
  const [feil, setFeil] = useState<FraHypotese['feil']>(null);
  const sisteBody = useRef<ProgramCreate | null>(null);
  const h = hypotese.data;
  const utfylling = useMemo(() => (h ? utfyllingFra(h) : null), [h]);
  const lukk = () => void naviger(tilbake.til, { state: tilbake.state });

  const lagre = (body: ProgramCreate) => {
    if (!h) return;
    sisteBody.current = body;
    setFeil(null);
    lag
      .mutateAsync({ hypoteseId: h.id, body })
      .then((p) => onLagret(p, nr))
      .catch((e: unknown) => setFeil(feilTekst(e, nrTekst, () => sisteBody.current && lagre(sisteBody.current))));
  };

  // Produktene også: skjemaet skal ikke fylles og fokuseres før det vet om hun må velge selv.
  if (hypotese.isPending || me.isPending || produkterTilstand === 'laster') return <Laster nrTekst={nrTekst} />;

  const tilbakeLenke = (
    <Link to={tilbake.til} state={tilbake.state} className="panel-knapp-sekundaer">
      Tilbake til {nrTekst}
    </Link>
  );
  if (hypotese.isError) {
    return (
      <Melding nrTekst={nrTekst} testid="prg-fra-feil">
        <Varsel tone="info" handling={<button type="button" className="panel-knapp-lenke" onClick={() => void hypotese.refetch()}>Prøv igjen</button>}>
          {`Fikk ikke hentet ${nrTekst}. Ingenting er fylt ut eller lagret.`}
        </Varsel>
        {tilbakeLenke}
      </Melding>
    );
  }
  // Tjeneren vokter alt dette (404, 403 not_owner, 403 insufficient_role, 409); her forklares det først.
  const grunn = !h
    ? `Fant ikke ${nrTekst}. Den finnes ikke, eller den er ikke din å se.`
    : h.owner?.id !== me.data?.id
      ? `Bare eieren av ${nrTekst} lager et betaprogram fra den.`
      : h.status !== 'valgt' && h.status !== 'testes'
        ? `${nrTekst} er ikke valgt eller testes. Et betaprogram lages fra en hypotese i Valgt eller Testes.`
        : !tilgangFra(me.data).kanSkrive(INNSPILL)
          ? 'Programmer lages av en behandler i hele Innspill.'
          : null;
  if (grunn || !h || !utfylling) {
    return (
      <Melding nrTekst={nrTekst} testid="prg-fra-laast">
        <Varsel tone="info" rolle="status">
          {grunn ?? `Fant ikke ${nrTekst}.`}
        </Varsel>
        {tilbakeLenke}
      </Melding>
    );
  }

  return (
    <Skjema
      modus={{ navn: 'ny' }}
      fraHypotese={{ nr, tittel: h.title ?? '', utfylling, tilbake, feil }}
      produkter={produkter}
      produkterTilstand={produkterTilstand}
      maksKontakterTak={maksKontakterTak}
      minorsPresent={minorsPresent}
      innstillingerTilstand={innstillingerTilstand}
      kanSkrive
      opptatt={lag.isPending}
      onOpprett={lagre}
      onOppdater={() => undefined}
      onAapne={() => undefined}
      onAvbryt={lukk}
    />
  );
}

/** Svaret fra tjeneren i klientens ord (skjerm 20). Serverens `message` vises aldri. */
function feilTekst(e: unknown, nrTekst: string, igjen: () => void): NonNullable<FraHypotese['feil']> {
  if (!erApiFeil(e) || erNettfeil(e) || e.status >= 500) return { tekst: 'Utkastet ble ikke lagret — tjeneren svarte ikke. Det du skrev står.', onProevIgjen: igjen };
  if (e.code === 'invalid_status_transition') return { tekst: `${nrTekst} er ikke lenger valgt eller testes. Utkastet er ikke lagret.` };
  if (e.code === 'not_owner') return { tekst: `Du er ikke lenger eier av ${nrTekst}.` };
  if (e.code === 'insufficient_role') return { tekst: 'Du er ikke lenger behandler i Innspill.' };
  if (e.code === 'hypothesis_not_found') return { tekst: `${nrTekst} finnes ikke lenger. Utkastet er ikke lagret.` };
  return { tekst: `Utkastet ble ikke lagret. ${e.brukertekst}` };
}

function Melding({ nrTekst, testid, children }: { nrTekst: string; testid: string; children: ReactNode }) {
  return (
    <aside aria-labelledby="prg-skjema-h" className="panel-prg-skjema" data-testid={testid}>
      <div className="panel-prg-kropp">
        <h2 id="prg-skjema-h" className="panel-prg-tittel">
          Nytt program fra {nrTekst}
        </h2>
        {children}
      </div>
    </aside>
  );
}

/** Skjerm 19: etikettene fra første øyeblikk, låste felt med skjelett, og foten låst med «Henter H-142 …». */
function Laster({ nrTekst }: { nrTekst: string }) {
  const etiketter = ['Funksjonen, i testerens ord', 'Produkt', 'Hypotese', 'Vi har rett hvis', 'er'];
  return (
    <aside aria-labelledby="prg-skjema-h" aria-busy="true" className="panel-prg-skjema" data-testid="prg-fra-laster">
      <div className="panel-prg-kropp">
        <h2 id="prg-skjema-h" className="panel-prg-tittel">
          Nytt program
        </h2>
        <div className="panel-prg-felter" role="status" aria-label={`Henter ${nrTekst}`}>
          {etiketter.map((e) => (
            <div key={e} className={`panel-felt${e === 'Hypotese' ? ' panel-prg-bred' : ''}`}>
              <span className="panel-felt-navn">{e}</span>
              <span className="panel-skjelett-linje" />
            </div>
          ))}
        </div>
      </div>
      <footer className="panel-prg-fot">
        <div className="panel-prg-knapper">
          <Knapp label="Lagre utkast" primaer onClick={() => undefined} grunn={`Henter ${nrTekst} …`} testid="prg-lagre" />
        </div>
      </footer>
    </aside>
  );
}
